import { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { addDays } from 'date-fns'
import { range, sortBy, find } from 'lodash'

import useApi from 'components/Hooks/useApi'
import { Format } from 'services/Dates'
import { parseShiftInfoWithoutTranslation } from 'services/Utilities'
import { createErrorNotification } from 'store/actions/notifications'
import { Select } from 'components/Forms'

import { t } from 'translations'

type Props = {
  module: string
  userId: string
  setFieldValue: (field: string, value: unknown) => void
}

type ShiftType = MData.Event | { _id: string; start: string }
type OptionType = ShiftType & { name: string }

const AttendanceShiftPicker: FC<Props> = ({ module: moduleName, userId, setFieldValue }) => {
  const dispatch = useDispatch()
  const { createAttendanceWithoutShift, numberOfDaysInAdvanceOfAttendanceRequest } = useSelector((state: MStore.RootState) => state.settings)
  const [options, setOptions] = useState<OptionType[]>([])

  const { handleApi: fetchShift, isLoading } = useApi({ method: 'GET', url: `/schedule/attendance?userId=${userId}` })

  // Loads the shift information
  useEffect(() => {
    async function loadShift() {
      const { response, isError }: { response: MData.Event[] | null; isError: boolean } = await fetchShift()
      if (isError) return dispatch(createErrorNotification())
      let result: ShiftType[] = response ? [...response] : []
      if (createAttendanceWithoutShift && response) result = addDatesWithoutShifts(response)
      const options = [...result.map(shift => ({ ...shift, name: parseShiftInfoWithoutTranslation(shift) }))]
      setOptions(options)
    }

    // Only set the first shift if there is one AND if we are creating
    // If user can create an attendance request without a shift due to createAttendanceWithoutShift
    // insert dummy date events for days up to and including numberOfDaysInAdvanceOfAttendanceRequest
    // if list does not already contain events for current those days
    const addDatesWithoutShifts = (shifts: ShiftType[]) => {
      const newShifts = [...shifts]
      // Iterate over range of 0 to numberOfDaysInAdvanceOfAttendanceRequest and add corresponding dummy events
      range(numberOfDaysInAdvanceOfAttendanceRequest + 1).forEach(daysToAdd => {
        const startToSearch = Format(addDays(new Date(), daysToAdd), { outFormat: 'date' })
        const eventsContainDate = find(newShifts, ({ start }) => start.includes(startToSearch))
        // If a shift for that date already exists in list, skip creating a dummy date event and increment
        if (eventsContainDate) return

        // Create a dummy event only containing the date and add it to the events list
        const start = Format(addDays(new Date(), daysToAdd), { outFormat: 'date' }) + 'T00:00:00'
        const absenceDummyEvent = { _id: `${daysToAdd}`, start }
        newShifts.push(absenceDummyEvent)
      })
      return sortBy(newShifts, ['start'])
    }

    if (userId && userId !== '') loadShift()
    if (userId === '') setOptions([])
  }, [userId, createAttendanceWithoutShift, numberOfDaysInAdvanceOfAttendanceRequest, fetchShift, dispatch])

  const optionsArray = [
    {
      _id: '',
      disabled: true,
      name: t(`${moduleName}.NO_SHIFT_SELECTED`)
    },
    ...options
  ]

  return (
    <Select
      name="eventId"
      module={moduleName}
      options={optionsArray}
      emptyPlaceholder
      required
      disabled={isLoading || !userId}
      handleChange={({ target: { value } }) => {
        if (!isNaN(value)) {
          // Find back the created shift and set the start into the attendance
          const start: string | undefined = find(options, { _id: value })?.start
          if (start) setFieldValue('start', start)
          setFieldValue('eventId', value)
        } else {
          setFieldValue('eventId', value)
        }
      }}
    />
  )
}

export default AttendanceShiftPicker
